import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

type RelatedLink = {
  to: string;
  title: string;
  description: string;
};

const ALL_LINKS: RelatedLink[] = [
  {
    to: "/compliance-services",
    title: "Compliance & Services",
    description:
      "Exactly what we do and do not do — legal advice on non-reserved matters, compliance and legal opinions.",
  },
  {
    to: "/services",
    title: "All legal & compliance services",
    description:
      "Labour, corporate, contracts, POPIA and company secretarial support in one place.",
  },
  {
    to: "/corporate-training",
    title: "Corporate training",
    description:
      "Workshops on labour law, POPIA, OHSA and workplace policy for teams and managers.",
  },
  {
    to: "/guides",
    title: "Practical guides",
    description:
      "Step-by-step guides on CCMA preparation, contracts, compliance and employee rights.",
  },
  {
    to: "/cv-builder",
    title: "AI CV Builder",
    description:
      "Build an ATS-ready CV and Z83 application pack with human-reviewed output.",
  },
  {
    to: "/credits",
    title: "Credits & pricing",
    description:
      "Top up credits for AI tools, document drafting and premium downloads.",
  },
  {
    to: "/founding-partners",
    title: "Founding partners",
    description:
      "Partner with SA LexPractiq on legal education, placements and practical training.",
  },
];

export function RelatedServices({
  exclude = [],
  limit = 3,
  title = "Related services",
  description = "Explore other areas of our legal-support and compliance practice.",
  className = "",
}: {
  exclude?: string[];
  limit?: number;
  title?: string;
  description?: string;
  className?: string;
}) {
  const links = ALL_LINKS.filter((l) => !exclude.includes(l.to)).slice(0, limit);
  if (links.length === 0) return null;

  return (
    <section
      aria-labelledby="related-services-heading"
      className={`container-narrow py-14 ${className}`}
    >
      <span className="gold-rule" />
      <h2
        id="related-services-heading"
        className="font-display text-2xl text-navy"
      >
        {title}
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        {description}
      </p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to as never}
            className="group rounded-xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-lifted"
          >
            <h3 className="font-display text-lg text-navy">{l.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {l.description}
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold">
              Learn more <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
